// # EGP Revenue Over Time Calculator
import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../../../firebase/config';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Area
} from 'recharts';
import './egp_rev_ovtime.css';

// # Define COLORS array for charts
const COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', 
  '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57', '#83a6ed', '#8dd1e1'
];

const EgpRevenueOverTime = ({ dateRange, onDataProcessed }) => {
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState({
    monthly: [],
    quarterly: [],
    yearly: []
  });
  const [productData, setProductData] = useState([]);
  const [nationalityData, setNationalityData] = useState([]);
  const [viewMode, setViewMode] = useState('monthly');
  const [error, setError] = useState(null);

  // # Fetch and process payment data
  useEffect(() => {
    const fetchRevenueData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // # Get all documents from the financial collection
        const financialRef = collection(db, 'financial');
        const financialSnap = await getDocs(financialRef);
        
        // # Extract all payment records
        let allPayments = [];
        
        // # Process each document
        financialSnap.docs.forEach(doc => {
          // # Document ID now contains year_month format (e.g., "2024_03")
          const docId = doc.id;
          const yearMonthData = doc.data();
          
          // # Check if payments map exists in the document
          if (yearMonthData.payments) {
            // # Extract payments from the payments map
            Object.entries(yearMonthData.payments).forEach(([paymentId, paymentData]) => {
              // # Only process payment transactions
              if (paymentId.startsWith('payment_') && paymentData.transaction_type === 'payment') {
                // # Extract payment date from metadata
                const paymentDate = new Date(paymentData.metadata?.created_at || '');
              
                // # Apply date range filter if provided
                if (dateRange) {
                  const startDate = new Date(dateRange.startDate);
                  const endDate = new Date(dateRange.endDate);
                  
                  // # Set hours for proper date comparison
                  startDate.setHours(0, 0, 0, 0);
                  endDate.setHours(23, 59, 59, 999);
                  
                  // # Skip if outside date range
                  if (paymentDate < startDate || paymentDate > endDate) {
                    return;
                  }
                }
                
                // # Add payment to collection with all necessary fields - directly use stored values
                allPayments.push({
                  id: paymentId,
                  doc_id: docId,
                  timestamp: paymentDate,
                  egp_total: parseFloat(paymentData.pay?.egp_total) || 0,
                  egp_net: parseFloat(paymentData.pay?.egp_net) || 0,
                  product: paymentData.pay?.product || 'unknown',
                  duration: paymentData.pay?.duration || '',
                  budget_dvd_amount: paymentData.budget_dvd?.egp_amount || 0,
                  budget_dvd_percent: paymentData.budget_dvd?.percentage_of_total || 0,
                  nationality: paymentData.user?.nationality || 'Unknown',
                  // # Directly use the shares data without recalculation
                  shares: paymentData.shares || {}
                });
              }
            });
          }
        });
        
        // # Process the data for different time periods
        processMonthlyData(allPayments);
        processQuarterlyData(allPayments);
        processYearlyData(allPayments);
        processProductData(allPayments);
        processNationalityData(allPayments);
        
        // # Call the onDataProcessed callback if provided
        if (onDataProcessed) {
          onDataProcessed(allPayments);
        }
      } catch (error) {
        console.error('Error fetching revenue data:', error);
        setError('Failed to load revenue data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchRevenueData();
  }, [dateRange, onDataProcessed]);
  
  // # Process monthly data - simplified to use pre-calculated values
  const processMonthlyData = (payments) => {
    // # Group payments by month
    const monthlyData = {};
    
    payments.forEach(payment => {
      const date = new Date(payment.timestamp);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      // # Initialize month data if not exists
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthKey,
          revenue: 0,
          netRevenue: 0, // # Add net revenue field
          count: 0
        };
      }
      
      // # Use the pre-calculated egp_total directly
      monthlyData[monthKey].revenue += payment.egp_total;
      // # Add net revenue calculation (total - budget)
      monthlyData[monthKey].netRevenue += payment.egp_net || (payment.egp_total - payment.budget_dvd_amount);
      monthlyData[monthKey].count += 1;
    });
    
    // # Convert to array and sort by month
    const monthlyDataArray = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
    
    // # Update state
    setRevenueData(prevData => ({
      ...prevData,
      monthly: monthlyDataArray
    }));
  };
  
  // # Process quarterly revenue data
  const processQuarterlyData = (payments) => {
    const quarterlyRevenue = {};
    
    payments.forEach(payment => {
      const date = payment.timestamp;
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      const quarterKey = `${date.getFullYear()}-Q${quarter}`;
      
      // # Initialize quarter if not exists
      if (!quarterlyRevenue[quarterKey]) {
        quarterlyRevenue[quarterKey] = {
          quarter: quarterKey,
          revenue: 0,
          netRevenue: 0, // # Add net revenue field
          count: 0
        };
      }
      
      // # Add payment amount
      quarterlyRevenue[quarterKey].revenue += payment.egp_total;
      // # Add net revenue calculation (total - budget)
      quarterlyRevenue[quarterKey].netRevenue += payment.egp_net || (payment.egp_total - payment.budget_dvd_amount);
      quarterlyRevenue[quarterKey].count += 1;
    });
    
    // # Convert to array and sort by quarter
    const quarterlyDataArray = Object.values(quarterlyRevenue)
      .sort((a, b) => a.quarter.localeCompare(b.quarter));
    
    // # Update state
    setRevenueData(prevData => ({
      ...prevData,
      quarterly: quarterlyDataArray
    }));
  };
  
  // # Process yearly revenue data
  const processYearlyData = (payments) => {
    const yearlyRevenue = {};
    
    payments.forEach(payment => {
      const year = payment.timestamp.getFullYear();
      
      // # Initialize year if not exists
      if (!yearlyRevenue[year]) {
        yearlyRevenue[year] = {
          year: year.toString(),
          revenue: 0,
          netRevenue: 0, // # Add net revenue field
          count: 0
        };
      }
      
      // # Add payment amount
      yearlyRevenue[year].revenue += payment.egp_total;
      // # Add net revenue calculation (total - budget)
      yearlyRevenue[year].netRevenue += payment.egp_net || (payment.egp_total - payment.budget_dvd_amount);
      yearlyRevenue[year].count += 1;
    });
    
    // # Convert to array and sort by year
    const yearlyDataArray = Object.values(yearlyRevenue)
      .sort((a, b) => a.year.localeCompare(b.year));
    
    // # Update state
    setRevenueData(prevData => ({
      ...prevData,
      yearly: yearlyDataArray
    }));
  };
  
  // # Process product-based revenue data
  const processProductData = (payments) => {
    // # Group payments by product
    const productRevenue = {};
    
    payments.forEach(payment => {
      const product = payment.product || 'unknown';
      
      // # Initialize product if not exists
      if (!productRevenue[product]) {
        productRevenue[product] = {
          product: formatProductName(product),
          revenue: 0,
          count: 0
        };
      }
      
      // # Add payment amount
      productRevenue[product].revenue += payment.egp_total;
      productRevenue[product].count += 1;
    });
    
    // # Convert to array and sort by revenue (descending)
    const productDataArray = Object.values(productRevenue)
      .sort((a, b) => b.revenue - a.revenue);
    
    // # Update state
    setProductData(productDataArray);
  };
  
  // # Process nationality-based revenue data
  const processNationalityData = (payments) => {
    // # Group payments by nationality
    const nationalityRevenue = {};
    
    payments.forEach(payment => {
      // # Normalize nationality or use 'Unknown'
      let nationality = payment.nationality || 'Unknown';
      
      // # Handle common variations
      if (nationality.toLowerCase().includes('egypt')) {
        nationality = 'Egypt';
      } else if (nationality.toLowerCase().includes('saudi')) {
        nationality = 'Saudi Arabia';
      } else if (nationality.toLowerCase().includes('emirates') || nationality.toLowerCase().includes('uae')) {
        nationality = 'UAE';
      }
      
      // # Initialize nationality if not exists
      if (!nationalityRevenue[nationality]) {
        nationalityRevenue[nationality] = {
          nationality: nationality,
          revenue: 0,
          count: 0
        };
      }
      
      // # Add payment amount
      nationalityRevenue[nationality].revenue += payment.egp_total;
      nationalityRevenue[nationality].count += 1;
    });
    
    // # Convert to array and sort by revenue (descending)
    const nationalityDataArray = Object.values(nationalityRevenue)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10); // # Limit to top 10 nationalities
    
    // # Update state
    setNationalityData(nationalityDataArray);
  };
  
  // # Format product name for display
  const formatProductName = (productName) => {
    switch (productName) {
      case 'step1_library':
        return 'Step 1 Library';
      case 'step2_library':
        return 'Step 2 Library';
      case 'step1_mentorship':
        return 'Step 1 Mentorship';
      case 'step2_mentorship':
        return 'Step 2 Mentorship';
      case 'step1_premium':
        return 'Step 1 Premium';
      case 'step2_premium':
        return 'Step 2 Premium';
      case 'step1_mentorship_premium':
        return 'Step 1 Mentorship (Premium)';
      case 'step2_mentorship_premium':
        return 'Step 2 Mentorship (Premium)';
      default:
        return productName;
    }
  };
  
  // # Get product color based on product type
  const getProductColor = (product) => {
    switch (true) {
      case product.includes('Library'):
        return '#8884d8'; // Purple
      case product.includes('Mentorship') && !product.includes('Premium'):
        return '#82ca9d'; // Green
      case product.includes('Premium'):
        return '#ffc658'; // Yellow
      default:
        return '#ff8042'; // Orange (default)
    }
  };
  
  // # Format tooltip values
  const formatTooltipValue = (value) => {
    return `E£ ${value.toLocaleString()}`;
  };
  
  // # Get chart data based on view mode
  const getChartData = () => {
    switch (viewMode) {
      case 'monthly':
        return revenueData.monthly;
      case 'quarterly':
        return revenueData.quarterly;
      case 'yearly':
        return revenueData.yearly;
      default:
        return revenueData.monthly;
    }
  };
  
  // # Get x-axis data key based on view mode
  const getXAxisDataKey = () => {
    switch (viewMode) {
      case 'monthly':
        return 'month';
      case 'quarterly':
        return 'quarter';
      case 'yearly':
        return 'year';
      default:
        return 'month';
    }
  };
  
  // # Calculate total revenue
  const calculateTotalRevenue = () => {
    const data = getChartData();
    return data.reduce((total, item) => total + item.revenue, 0);
  };
  
  // # Calculate total net revenue
  const calculateTotalNetRevenue = () => {
    const data = getChartData();
    return data.reduce((total, item) => total + item.netRevenue, 0);
  };
  
  // # Calculate average revenue
  const calculateAverageRevenue = () => {
    const data = getChartData();
    if (data.length === 0) return 0;
    return calculateTotalRevenue() / data.length;
  };
  
  // # Calculate growth rate
  const calculateGrowthRate = () => {
    const data = getChartData();
    if (data.length < 2) return null;
    
    const firstPeriod = data[0].revenue;
    const lastPeriod = data[data.length - 1].revenue;
    
    if (firstPeriod === 0) return null;
    
    return ((lastPeriod - firstPeriod) / firstPeriod) * 100;
  };

  return (
    <div className="egp_rev_ovtime_container">
      {error && (
        <div className="egp_rev_ovtime_error">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="egp_rev_ovtime_loading">
          Loading revenue data...
        </div>
      ) : (
        <>
          {/* View Mode Selector */}
          <div className="egp_rev_ovtime_controls">
            <div className="egp_rev_ovtime_view-selector">
              <button 
                className={`egp_rev_ovtime_view-button ${viewMode === 'monthly' ? 'active' : ''}`}
                onClick={() => setViewMode('monthly')}
              >
                Monthly
              </button>
              <button 
                className={`egp_rev_ovtime_view-button ${viewMode === 'quarterly' ? 'active' : ''}`}
                onClick={() => setViewMode('quarterly')}
              >
                Quarterly
              </button>
              <button 
                className={`egp_rev_ovtime_view-button ${viewMode === 'yearly' ? 'active' : ''}`}
                onClick={() => setViewMode('yearly')}
              >
                Yearly
              </button>
            </div>
          </div>
          
          {/* Stats Cards */}
          <div className="egp_rev_ovtime_stats">
            <div className="egp_rev_ovtime_stat-card">
              <h3>Total Revenue</h3>
              <p className="egp_rev_ovtime_stat-value">
                E£ {calculateTotalRevenue().toLocaleString()}
              </p>
            </div>
            <div className="egp_rev_ovtime_stat-card">
              <h3>Total Net Revenue</h3>
              <p className="egp_rev_ovtime_stat-value">
                E£ {calculateTotalNetRevenue().toLocaleString()}
              </p>
            </div>
            <div className="egp_rev_ovtime_stat-card">
              <h3>Average per {viewMode.slice(0, -2)}</h3>
              <p className="egp_rev_ovtime_stat-value">
                E£ {calculateAverageRevenue().toLocaleString()}
              </p>
            </div>
            {calculateGrowthRate() !== null && (
              <div className="egp_rev_ovtime_stat-card">
                <h3>Growth Rate</h3>
                <p className={`egp_rev_ovtime_stat-value ${calculateGrowthRate() >= 0 ? 'positive' : 'negative'}`}>
                  {calculateGrowthRate() >= 0 ? '+' : ''}{calculateGrowthRate().toFixed(1)}%
                </p>
              </div>
            )}
          </div>
          
          {/* Line Chart */}
          <div className="egp_rev_ovtime_chart-container">
            <h3>EGP Total & Net EGP Revenue Over Time ({viewMode})</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey={getXAxisDataKey()} 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    // # Format x-axis labels based on view mode
                    if (viewMode === 'monthly') {
                      // # Show month and year (e.g., Jan 2024)
                      const [year, month] = value.split('-');
                      const date = new Date(year, parseInt(month) - 1);
                      return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
                    } else if (viewMode === 'quarterly') {
                      // # Show quarter and year (e.g., Q1 2024)
                      return value;
                    } else {
                      // # Show year
                      return value;
                    }
                  }}
                />
                <YAxis 
                  tickFormatter={(value) => `E£${value.toLocaleString()}`}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'Revenue (EGP)') {
                      return [`E£ ${value.toLocaleString()}`, 'Total Revenue'];
                    } else if (name === 'Net Revenue (EGP)') {
                      return [`E£ ${value.toLocaleString()}`, 'Net Revenue'];
                    }
                    return [`E£ ${value.toLocaleString()}`, name];
                  }}
                  labelFormatter={(value) => {
                    if (viewMode === 'monthly') {
                      const [year, month] = value.split('-');
                      const date = new Date(year, parseInt(month) - 1);
                      return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
                    } else if (viewMode === 'quarterly') {
                      const [year, quarter] = value.split('-');
                      return `${quarter} ${year}`;
                    } else {
                      return `Year ${value}`;
                    }
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#8884d8" 
                  name="Revenue (EGP)" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="netRevenue" 
                  stroke="#82ca9d" 
                  name="Net Revenue (EGP)" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          {/* Replace Bar Chart with Product Revenue Breakdown */}
          <div className="egp_rev_ovtime_chart-container">
            <h3>Total Revenue Breakdown by Product</h3>
            <div className="egp_rev_ovtime_chart-row">
              {/* Product Revenue Pie Chart */}
              <div className="egp_rev_ovtime_chart-col">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={productData}
                      dataKey="revenue"
                      nameKey="product"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    >
                      {productData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getProductColor(entry.product)} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`E£ ${value.toLocaleString()}`, 'Revenue']}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Product Revenue Bar Chart */}
              <div className="egp_rev_ovtime_chart-col">
            <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart
                    layout="vertical"
                    data={productData}
                    margin={{ top: 20, right: 20, bottom: 20, left: 100 }}
                  >
                <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(value) => `E£${value.toLocaleString()}`} />
                    <YAxis 
                      dataKey="product" 
                      type="category" 
                      scale="band" 
                      width={100}
                  tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'revenue') return [`E£ ${value.toLocaleString()}`, 'Revenue'];
                        return [value, 'Count'];
                      }}
                    />
                    <Legend />
                    <Bar dataKey="revenue" fill="#8884d8" name="Revenue (EGP)">
                      {productData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getProductColor(entry.product)} />
                      ))}
                    </Bar>
                    <Line dataKey="count" stroke="#ff7300" name="Number of Payments" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {/* Add Nationality Revenue Breakdown */}
          <div className="egp_rev_ovtime_chart-container">
            <h3>Total Revenue Breakdown by Nationality (Top 10)</h3>
            <div className="egp_rev_ovtime_chart-row">
              {/* Nationality Revenue Pie Chart */}
              <div className="egp_rev_ovtime_chart-col">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={nationalityData}
                      dataKey="revenue"
                      nameKey="nationality"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    >
                      {nationalityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`E£ ${value.toLocaleString()}`, 'Revenue']}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Nationality Revenue Bar Chart */}
              <div className="egp_rev_ovtime_chart-col">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={nationalityData}
                    layout="vertical"
                    margin={{ top: 20, right: 20, bottom: 20, left: 80 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(value) => `E£${value.toLocaleString()}`} />
                <YAxis 
                      dataKey="nationality" 
                      type="category" 
                      scale="band" 
                      width={80}
                      tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  formatter={(value) => [`E£ ${value.toLocaleString()}`, 'Revenue']}
                />
                <Legend />
                    <Bar dataKey="revenue" name="Revenue (EGP)">
                      {nationalityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
              </BarChart>
            </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EgpRevenueOverTime;
